import { useMemo } from 'react';
import { ODataDsnsService } from '../services/ODataDsnsService';
import { useLogger } from '../context/useLogger';
import { useAccessToken } from './useAccessToken';
import { useQuery } from '@tanstack/react-query';
import { DsnDetailsResponse } from '../types/dsn.types';

/**
 * Custom React hook to fetch DSN details for a given instance ID using ODataFrontendService.
 *
 * This hook leverages authentication via Auth0 and logs relevant information using the provided logger.
 * It memoizes the ODataFrontendService instance and uses React Query to manage the asynchronous fetch operation.
 *
 * @param instanceId - The unique identifier for the DSN instance whose details are to be fetched.
 * @returns The result of the query, including data, loading, and error states as provided by React Query.
 */
export function useQueryDsnDetails(instanceId: string | undefined) {
  const getAccessToken = useAccessToken();
  const logger = useLogger();

  const odataService = useMemo(
    () => new ODataDsnsService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const fetchDsnDetails = () => {
    if (!instanceId) {
      throw new Error('Instance ID is required');
    }
    const response: Promise<DsnDetailsResponse> = odataService.fetchDsnDetails(instanceId);

    return response;
  };

  logger.info('useQueryDsnDetails instanceId:', instanceId);

  const queryKey = ['query-get', 'DsnDetails', instanceId];
  const queryResult = useQuery({
    queryKey,
    queryFn: fetchDsnDetails,
    throwOnError: (_error, query) => query.state.data === undefined,
  });

  return queryResult;
}
